import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const rolesUI = ["technician", "client"];
const mapRoleToApi = (r) => (r === "technician" ? "admin" : "user");

// ✅ message_key → t(key)
const translateBackend = (t, data) => {
  if (!data) return t("msg_server_or_network");
  if (data.message_key) return t(data.message_key, data.message_params || {});
  return t("msg_unknown_error");
};

const UserRegister = () => {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [roleUI, setRoleUI] = useState("technician");

  const [specialty, setSpecialty] = useState("");
  const [sector, setSector] = useState("");

  const [application, setApplication] = useState("");
  const [loading, setLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [refreshUsers, setRefreshUsers] = useState(false);

  // ✅ same method as HelpPage: messages in-page (no toast)
  // submit message (add user)
  const [addMsg, setAddMsg] = useState({ type: "", text: "" }); // "error" | "success" | "info" | ""
  // list message (fetch/delete)
  const [listMsg, setListMsg] = useState({ type: "", text: "" }); // "error" | "success" | "info" | ""

  useEffect(() => {
    const storedApp = localStorage.getItem("userApplication") || "";
    setApplication(storedApp);
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!application) return;

      setListMsg({ type: "", text: "" });

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/get_users`,
          { params: { application } }
        );

        if (response.data?.success) {
          setUsers(response.data.users || []);
        } else {
          setListMsg({ type: "error", text: translateBackend(t, response.data) });
        }
      } catch (err) {
        setListMsg({ type: "error", text: translateBackend(t, err?.response?.data) });
      }
    };

    fetchUsers();
  }, [refreshUsers, application, t]);

  useEffect(() => {
    if (roleUI !== "technician") {
      setSpecialty("");
      setSector("");
    }
  }, [roleUI]);

  const prettyRole = (r) =>
    r === "admin" ? t("role_technician") : r === "user" ? t("role_client") : r;

  const msgClass = (type) =>
    type === "error"
      ? "rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      : type === "success"
      ? "rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success"
      : "rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary";

  const handleSubmit = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // clear messages
    setAddMsg({ type: "", text: "" });
    setListMsg({ type: "", text: "" });

    if (!email || !username || !roleUI || !application) {
      setAddMsg({ type: "error", text: t("all_fields_required") });
      return;
    }

    if (roleUI === "technician" && (!specialty.trim() || !sector.trim())) {
      setAddMsg({ type: "error", text: t("tech_fields_required") });
      return;
    }

    if (!emailRegex.test(email)) {
      setAddMsg({ type: "error", text: t("msg_invalid_email") });
      return;
    }

    const role = mapRoleToApi(roleUI);
    setLoading(true);

    try {
      const payload = {
        email: email.trim(),
        username: username.trim(),
        role,
        application: application.trim(),
        ...(roleUI === "technician"
          ? { specialty: specialty.trim(), sector: sector.trim() }
          : {}),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/register_user`,
        payload
      );

      if (response.data?.success) {
        setAddMsg({
          type: "success",
          text: response.data?.message_key ? t(response.data.message_key) : t("user_registered"),
        });

        setEmail("");
        setUsername("");
        setRoleUI("technician");
        setSpecialty("");
        setSector("");

        setRefreshUsers((prev) => !prev);
      } else {
        const data = response.data;

        // ✅ optional: treat some keys as INFO (blue) like HelpPage
        // example if backend uses "users_not_found" or similar
        if (data?.message_key === "users_not_found") {
          setAddMsg({ type: "info", text: t(data.message_key) });
        } else {
          setAddMsg({ type: "error", text: translateBackend(t, data) });
        }
      }
    } catch (err) {
      const data = err?.response?.data;

      // ✅ optional: treat some keys as INFO (blue)
      if (data?.message_key === "users_not_found") {
        setAddMsg({ type: "info", text: t(data.message_key) });
      } else {
        setAddMsg({ type: "error", text: translateBackend(t, data) });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirm_delete_user"))) return;

    // clear messages
    setAddMsg({ type: "", text: "" });
    setListMsg({ type: "", text: "" });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/delete_user`,
        { id }
      );

      if (response.data?.success) {
        setListMsg({
          type: "success",
          text: response.data?.message_key ? t(response.data.message_key) : t("user_deleted"),
        });
        setRefreshUsers((prev) => !prev);
      } else {
        setListMsg({ type: "error", text: translateBackend(t, response.data) });
      }
    } catch (err) {
      setListMsg({ type: "error", text: translateBackend(t, err?.response?.data) });
    }
  };

  const inputClass =
    "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20";

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold text-foreground">{t("add_user")}</h1>

      <div className="mt-6 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!loading) handleSubmit();
            }}
            className="rounded-xl border border-border bg-card p-6"
            noValidate
          >
            {addMsg.text && (
              <div className={`mb-4 ${msgClass(addMsg.type)}`}>{addMsg.text}</div>
            )}

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t("email")} *</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} required />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t("username")} *</label>
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className={inputClass} required />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t("role")}</label>
                <select value={roleUI} onChange={(e) => setRoleUI(e.target.value)} className={inputClass}>
                  {rolesUI.map((r) => (
                    <option key={r} value={r}>
                      {r === "technician" ? t("role_technician") : t("role_client")}
                    </option>
                  ))}
                </select>
              </div>

              {roleUI === "technician" && (
                <>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">{t("specialty")}</label>
                    <input type="text" value={specialty} onChange={(e) => setSpecialty(e.target.value)} className={inputClass} required />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">{t("sector")}</label>
                    <input type="text" value={sector} onChange={(e) => setSector(e.target.value)} className={inputClass} required />
                  </div>
                </>
              )}
            </div>

            <button type="submit" disabled={loading} className="mt-6 w-full rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {loading ? t("registering") : t("register")}
            </button>
          </form>
        </div>

        <div className="lg:col-span-2">
          {listMsg.text && <div className={`mb-4 ${msgClass(listMsg.type)}`}>{listMsg.text}</div>}
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("email")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("username")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("role")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("specialty")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("sector")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("status")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-6 text-center text-muted-foreground">{t("no_users")}</td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 text-foreground">{user.email}</td>
                      <td className="px-4 py-3 text-foreground">{user.username}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">{prettyRole(user.role)}</span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{user.specialty || "-"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{user.sector || "-"}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${user.is_activated === 1 ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
                          {user.is_activated === 1 ? t("active") : t("not_active")}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleDelete(user.id)} className="text-xs text-destructive hover:underline">
                          {t("delete")}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRegister;
